import { appLanguage } from '@orientaction/reducers';
import { isYoutubeUrl } from '@orientaction/utils';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useSelector } from 'react-redux';
import YoutubeIframe, { YoutubeIframeRef } from '../../../../../components/VideoYoutubeEmbed';
import { localizedVideo } from '../VideoImport/bilingual';
import RedesignImage from '../RedesignImage';

export interface MediaVideoPlayerRef {
  pauseVideo: () => void;
}

export type MediaVideoPlayerProps = {
  imageUrl?: string | null;
  videoUrl?: string | null;
  videoEn?: string | null;
  autoPlayVideoEn?: number | null;
  muteVideoEn?: number | null;

  autoPlayVideo?: number | null;
  muteVideo?: number | null;
  imageAlt?: string;
  imageClassName?: string;
  imageContainerClassName?: string;
  videoClassName?: string;
};

const MediaVideoPlayer = forwardRef<MediaVideoPlayerRef, MediaVideoPlayerProps>(
  (
    {
      imageUrl,
      videoUrl: frenchVideo,
      videoEn,
      autoPlayVideoEn,
      muteVideoEn,
      autoPlayVideo: frenchAutoPlay = 0,
      muteVideo: frenchMute = 0,
      imageAlt = 'media',
      imageClassName,
      imageContainerClassName,
      videoClassName,
    },
    ref
  ) => {
    const language = useSelector(appLanguage);
    const {
      video: videoUrl,
      autoPlayVideo,
      muteVideo,
    } = localizedVideo(
      {
        video: frenchVideo,
        videoEn,
        autoPlayVideo: frenchAutoPlay,
        muteVideo: frenchMute,
        autoPlayVideoEn,
        muteVideoEn,
      },
      language || 'FR'
    );
    const youtubeRef = useRef<YoutubeIframeRef>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    useImperativeHandle(ref, () => ({
      pauseVideo: () => {
        youtubeRef.current?.pauseVideo();
        videoRef.current?.pause();
      },
    }));

    if (videoUrl) {
      if (isYoutubeUrl(videoUrl)) {
        return (
          <YoutubeIframe
            ref={youtubeRef}
            key={videoUrl}
            url={videoUrl}
            autoPlay={autoPlayVideo || 0}
            mute={muteVideo === 1 ? 0 : 1}
          />
        );
      }

      return (
        <video
          ref={videoRef}
          key={videoUrl}
          className={videoClassName}
          src={videoUrl}
          controls={true}
          autoPlay={autoPlayVideo === 1}
          playsInline={true}
          style={{ width: '100%', display: 'block', borderRadius: 5 }}
          muted={muteVideo !== 1}>
          <track kind="captions" />
        </video>
      );
    }

    if (imageUrl) {
      return (
        <div className={imageContainerClassName}>
          <RedesignImage className={imageClassName} src={imageUrl} alt={imageAlt} />
        </div>
      );
    }

    return null;
  }
);

MediaVideoPlayer.displayName = 'MediaVideoPlayer';

export default MediaVideoPlayer;
